import { ActivatedChallengePaymentDetails, AdditionalActivatedChallengeFields, ChallengeType, ChargebeeTransactionStatus, GetActivatedChallengeResponse, GetChallengeResponse, OrganizationPurchaseHistory, RegistrationField } from '../types';
import { LitElement, css, html } from 'lit';
import { customElement, property, query, state } from 'lit/decorators';
import { isDefined, undefinedIfEmpty } from '../helpers/utils';

import { Dialog } from '@material/mwc-dialog';
import { addActivatedChallenge } from '../helpers/api/challenge-api';
import challengeData from '../helpers/state/challenge-data';
import { getMainPage } from '../helpers/page-name';
import topicData from '../helpers/state/topic-data';

@customElement('challenge-participants-progress-modal')
export class ChallengeParticipantsProgressModal extends LitElement {

    @query('#search')
    searchInput!: HTMLInputElement;

    @query('#addUserPopUp')
    addUserPopUp!: Dialog;

    @query('#new_user_email')
    newUserEmail!: HTMLInputElement;

    @query('#new_user_name')
    newUserName!: HTMLInputElement;

    @query('#new_user_phone')
    newUserPhone!: HTMLInputElement;

    @property({ type: Array })
    data: GetActivatedChallengeResponse[] = [];

    @property({ type: Object })
    challenge_data?: GetChallengeResponse;

    @state()
    purchaseHistory: OrganizationPurchaseHistory | undefined;

    @state()
    sortBy: string = '';

    @state()
    sortOrder: 'asc' | 'desc' = 'asc';

    @state()
    activatedChallenges: GetActivatedChallengeResponse[] = [];

    amplitude_callsite: string = 'Challenge Participants Progress Modal';

    async updated(changedProperties: any) {
        changedProperties.forEach((oldValue: any, propName: any) => {
            if(propName === 'data') {
                // get new data and sort by number of points
                this.activatedChallenges = this.data.sort((a, b) => {
                    return b.point_count - a.point_count;
                }) ?? [];
            }
        });
    }

    handleClickDownload() {
        let content = '';

        let additional_fields: string[] = [];
        if (challengeData.challenge?.registration_settings?.additional_fields) {
            additional_fields = challengeData.challenge.registration_settings.additional_fields.map((field) => field.label);
        }

        content +=
            `Id,"Challenge Id",Name,Email,Phone,"Checkin Count","Point Count","Created At","Updated At" ${additional_fields.map((field) => `,${field}`).join('')}\n`;

        for (const activatedChallenge of this.activatedChallenges) {
            content += `${activatedChallenge._id.$oid},${activatedChallenge.challenge_id.$oid},"${activatedChallenge?.display_name ?? ''}","${
                activatedChallenge?.email ?? ''
            }","${activatedChallenge?.phone_number ?? ''}",${activatedChallenge.checkin_count},${activatedChallenge.point_count},"${activatedChallenge.created_at ?? ''}","${
                activatedChallenge.updated_at ?? ''
            }" ${additional_fields.map((field) => `,"${activatedChallenge?.additional_fields?.find((f) => f.field_name === field)?.field_value ?? ''}"`).join('')}\n`;
        }

        const filename = 'activated_challenges.csv';

        const blob = new Blob([content], {
            type: 'text/plain;charset=utf-8',
        });

        const a = document.createElement('a');
        document.body.appendChild(a);
        a.style.display = 'none';

        const url = window.URL.createObjectURL(blob);
        a.href = url;
        a.download = filename;
        a.click();
        window.URL.revokeObjectURL(url);
    }

    handleClickSave() {
        this.dispatchEvent(
            new CustomEvent('on-save')
        );
    }

    handleAddUserManually() {
        this.addUserPopUp.show();
    }

    handleClickViewMore(challenge: GetActivatedChallengeResponse) {
        this.dispatchEvent(
            new CustomEvent('on-view-details', {
                detail: { data: challenge, },
                bubbles: true,
                composed: true
            })
        );
    }

    search(search_text: string) {
        if (isDefined(undefinedIfEmpty(search_text))) {
            this.activatedChallenges = this.data.filter((challenge) => {
                return ((challenge.display_name?.toLowerCase().includes(search_text.toLowerCase())) ?? false) || challenge.email?.toLowerCase().includes(search_text.toLowerCase());
            });
        } else {
            this.activatedChallenges = this.data;
        }
    }

    clearSearch() {
        this.activatedChallenges = this.data;
        this.searchInput.value = '';
    }

    async copyUserProgressLink(activated_challenge: GetActivatedChallengeResponse) {
        const link = activated_challenge.link_set?.activated_challenge_page_write_link;
        if (link) {
            await navigator.clipboard.writeText(topicData.swapUrlForOrgUrl(link)).then(() => {
                getMainPage().errorToastElement.showToast('Link copied to clipboard');
            }
            );
        } else {
            getMainPage().errorToastElement.showToast('Link not available');
        }

    }

    async handleClickAddNewUser() {
        const email = this.newUserEmail.value;
        const name = this.newUserName.value;
        const phone = this.newUserPhone.value;
        const additional_fields: AdditionalActivatedChallengeFields[] = [];
        if (challengeData.challenge?.registration_settings?.additional_fields) {
            challengeData.challenge.registration_settings.additional_fields.forEach((field: RegistrationField, index) => {
                const value = (this.shadowRoot?.querySelector(`#${`field-${index}`}`) as HTMLInputElement).value;
                additional_fields.push({ field_name: field.label, field_value: value });
            });
        }

        if (undefinedIfEmpty(email) === undefined && !undefinedIfEmpty(phone) === undefined) {
            getMainPage().errorToastElement.showToast('Please enter email or phone number');
            return;
        }
        if (this.challenge_data?._id?.$oid) {
            await addActivatedChallenge(this.amplitude_callsite, email, this.challenge_data?._id?.$oid, phone, name, false, additional_fields, topicData.topicKey);
        }
        this.addUserPopUp.close();

        this.dispatchEvent(
            new CustomEvent('refresh-challenges', {
                bubbles: true,
                composed: true
            })
        );
    }

    sortClickHandler(sortBy: string) {
        if (this.sortBy === sortBy) {
            this.sortOrder = this.sortOrder === 'asc' ? 'desc' : 'asc';
        } else {
            this.sortBy = sortBy;
            this.sortOrder = 'asc';
        }

        this.activatedChallenges = this.activatedChallenges.sort((a, b) => {
            let comparison = 0;
            if (sortBy === 'name') {
                comparison = (a.display_name ?? '').localeCompare(b.display_name ?? '');
            } else if (sortBy === 'email') {
                comparison = (a.email ?? '').localeCompare(b.email ?? '');
            } else if (sortBy === 'checkin') {
                comparison = a.checkin_count - b.checkin_count;
            } else if (sortBy === 'point') {
                comparison = a.point_count - b.point_count;
            } else if (sortBy === 'created') {
                comparison = new Date(a.created_at).getTime() - new Date(b.created_at).getTime();
            } else if (sortBy === 'updated') {
                comparison = new Date(a.updated_at).getTime() - new Date(b.updated_at).getTime();
            } else if (sortBy === 'visits') {
                comparison = (a.counted_checkins?.length ?? 0) - (b.counted_checkins?.length ?? 0);
            } else if (sortBy === 'saved') {
                comparison = (a.personal_locations?.saved.length ?? 0) - (b.personal_locations?.saved.length ?? 0);
            }

            return this.sortOrder === 'asc' ? comparison : -comparison;
        });
    }

    hasTransactionOrOverwritten(activated_challenge_id: string, payment_details?: ActivatedChallengePaymentDetails): boolean {
        // Check if admin overridden
        if (
            payment_details?.payment_status === ChargebeeTransactionStatus.AdminOverridden
        ) {
            return true;
        }

        // Check if transaction exists in purchase history
        if (!this.purchaseHistory?.transactions) {
            return false;
        }

        return this.purchaseHistory.transactions.some((transaction) =>
            transaction.purchase_records && 'ActivatedChallenge' in transaction.purchase_records && transaction.purchase_records?.ActivatedChallenge?.activated_challenge_id.$oid === activated_challenge_id
        );
    }

    render() {
        return html`
            <settings-card header="Registrants (${this.activatedChallenges.length})" .maxWidth=${'100%'}>
                <div class="modalContent">
                   <div class="challenges-wrapper">
                        <div class="header-row">
                            <div class="header-cell text" @click="${() => this.sortClickHandler('name')}">Username ${this.sortBy === 'name' ? html`<mwc-icon>${this.sortOrder === 'asc' ? 'arrow_drop_up' : 'arrow_drop_down'}</mwc-icon>` : ''}</div>
                            <div class="header-cell text primary" @click="${() => this.sortClickHandler('email')}">Email ${!challengeData.isType(ChallengeType.Guide) ? html`& Rewards` : ''} ${this.sortBy === 'email' ? html`<mwc-icon>${this.sortOrder === 'asc' ? 'arrow_drop_up' : 'arrow_drop_down'}</mwc-icon>` : ''}</div>
                            <div class="header-cell text" @click="${() => this.sortClickHandler('phone')}">Phone Number ${this.sortBy === 'phone' ? html`<mwc-icon>${this.sortOrder === 'asc' ? 'arrow_drop_up' : 'arrow_drop_down'}</mwc-icon>` : ''}</div>
                            <div class="header-cell number" @click="${() => this.sortClickHandler('checkin')}">Places ${this.sortBy === 'checkin' ? html`<mwc-icon>${this.sortOrder === 'asc' ? 'arrow_drop_up' : 'arrow_drop_down'}</mwc-icon>` : ''}</div>
                            <div class="header-cell number" @click="${() => this.sortClickHandler('visits')}">Visits ${this.sortBy === 'visits' ? html`<mwc-icon>${this.sortOrder === 'asc' ? 'arrow_drop_up' : 'arrow_drop_down'}</mwc-icon>` : ''}</div>
                            ${!challengeData.isType(ChallengeType.Guide) ? html`
                                <div class="header-cell number" @click="${() => this.sortClickHandler('point')}">Point Count ${this.sortBy === 'point' ? html`<mwc-icon>${this.sortOrder === 'asc' ? 'arrow_drop_up' : 'arrow_drop_down'}</mwc-icon>` : ''}</div>
                            ` : html`
                                <div class="header-cell number" @click="${() => this.sortClickHandler('saved')}">Saved Locations ${this.sortBy === 'saved' ? html`<mwc-icon>${this.sortOrder === 'asc' ? 'arrow_drop_up' : 'arrow_drop_down'}</mwc-icon>` : ''}</div>
                            `}
                            <div class="header-cell number" >Unique Link</div>
                        </div>
                        ${this.activatedChallenges.map((challenge) => html`
                            <div class="challenge-row" title="Click to see this participant's details" @click="${() => this.handleClickViewMore(challenge)}">
                                <div class="user data-cell text">${challenge.display_name ?? ''}</div>
                                <div class="email data-cell text primary">
                                    ${challenge.hidden_from_leaderboard ? html`<mwc-icon style="--mdc-icon-size: 16px;">visibility_off</mwc-icon>` : html``}
                                    ${this.hasTransactionOrOverwritten(challenge._id.$oid, challenge.payment_details) ? html`<mwc-icon style="--mdc-icon-size: 16px;">paid</mwc-icon>` : ''}
                                    <span style="margin-right: 5px;">
                                        ${challenge.email ?? ''}
                                    </span>
                                    ${challenge.earned_rewards?.length > 0 ?
                                        challenge.earned_rewards.map((reward) => html`
                                            <img src="${this.challenge_data?.rewards.find((rew) => rew?._id?.$oid === reward._id?.$oid)?.image_url ?? ''}" style="width: 20px; height: 20px; border-radius: 50%; margin-left: -2px; background-color: var(--theme-border-color); box-shadow: 0 0 5px 0 rgba(0, 0, 0, 0.2);">

                                        `) : ''
                                    }
                                </div>
                                <div class="phon data-cell text">${challenge.phone_number ?? 'None'}</div>
                                <div class="visits data-cell number">${challenge.checkin_locations?.length ?? 0}</div>
                                <div class="checkin data-cell number">${challenge.checkin_count}</div>
                                ${!challengeData.isType(ChallengeType.Guide) ? html`
                                    <div class="point data-cell number">
                                        <div style="font-weight: 700;">${challenge.point_count}</div>
                                    </div>
                                ` : html`
                                    <div class="point data-cell number">${challenge.personal_locations?.saved.length ?? 0}</div>
                                `}
                                <div class="data-cell number"> <form-button variant="outline" size="xs" .icon=${'content_copy'} class="aqua-blue" @click="${async (e: Event) => { e.stopPropagation(); await this.copyUserProgressLink(challenge); }}"></form-button></div>
                            </div>
                        `)}
                    </div>
                </div>
                <div class="header__actions" slot="header">
                    <form-button
                        size="md"
                        variant="outline"
                        class="action-button aqua-blue"
                        @click="${this.handleAddUserManually}"
                    >
                        Add
                    </form-button>
                    <form-button
                        variant="outline"
                        class="action-button aqua-blue"
                        @click="${this.handleClickDownload}"
                    >
                        Download List
                    </form-button>
                    <form-button
                        variant="outline"
                        class="action-button aqua-blue"
                        @click="${() => this.dispatchEvent(new CustomEvent('on-copy-embeddable-link', { bubbles: true, composed: true }))}"
                    >
                        Copy Leaderboard Page Link
                    </form-button>
                    <proxi-textfield
                        outlined
                        id="search"
                        label="Search"
                        placeholder="Search"
                        @change="${() => this.search(this.searchInput?.value)}"
                    ></proxi-textfield>
                    ${undefinedIfEmpty(this.searchInput?.value) ? html`
                    <mwc-icon class="aqua-blue" @click="${this.clearSearch}" style="cursor: pointer;">
                            close
                    </mwc-icon> ` : ''}
                </div>
            </settings-card>
            <mwc-dialog id="addUserPopUp" class="dialog" hideActions>
                <modal-dialog size="sm" @closed="${() => this.addUserPopUp.close()}">
                    <div slot="body">
                        <h3>Add Participant Manually</h3>
                        <p> Participants generally register themselves using the registration link, but you can use this form to add participants on their behalf. </p>
                        <proxi-textfield
                            outlined
                            id="new_user_email"
                            type="text"
                            label="Email"
                            placeholder=""
                            value=""
                        >
                        </proxi-textfield>
                        <proxi-textfield
                            outlined
                            id="new_user_name"
                            type="text"
                            label="Name"
                            placeholder=""
                            value=""
                        >
                        </proxi-textfield>
                        <proxi-textfield
                            outlined
                            id="new_user_phone"
                            type="phone"
                            label="Phone Number"
                            placeholder=""
                            value=""
                        >
                        </proxi-textfield>
                        <br>
                        ${challengeData.challenge?.registration_settings?.additional_fields?.map((field, index) => html`
                            <proxi-textfield
                                outlined
                                id="${`field-${index}`}"
                                type="text"
                                label="${field.label}"
                                placeholder=""
                                value=""
                            >
                            </proxi-textfield>
                            <br>
                        `)}
                        <form-button
                            variant="outline"
                            class="action-button aqua-blue"
                            @click="${this.handleClickAddNewUser}"
                        >
                            Add User
                        </form-button>
                    </div>
                </modal-dialog>
        </mwc-dialog>
        `;
    }


    static get styles() {
        return css`
            :host {
                --modal-width: 800px;
            }

            .modalContent {
                position: relative;
                width: 100%;
                max-height: calc(80vh - 180px);
                height: auto;
                overflow-y: auto;
                box-sizing: border-box;
            }

            .modal__header {
                width: 100%;
                text-align: center;
                padding: 0px 20px 5px;
                color: #000000;
                font-size: 18px;
                font-weight: 700;
                line-height: 130%;
                box-sizing: border-box;
            }

            .modal__content {
                padding: 20px 0 5px;
                width: 100%;
                box-sizing: border-box;
            }

            .header__actions {
                display: flex;
                flex-direction: row;
                align-items: center;
                justify-content: flex-end;
                gap: 10px;
                box-sizing: border-box;
            }



            .header-row {
                cursor: pointer;
                width: 100%;
                align-items: center;
                font-weight: 700;
                display: flex;
                height: 40px;
                background-color: var(--theme-border-color);
                gap: 10px;
            }

            .header-row mwc-icon {
                font-size: 20px;
            }

            .header-cell,
            .data-cell {
                align-items: center;
            }

            .header-cell {
                display: flex;
                align-items: center;
                padding: 0 10px;
            }

            .header-cell.text {
                text-align: left;
                width: 15%;
                overflow: hidden;
                text-overflow: ellipsis;
            }

            .header-cell.number {
                text-align: center;
                width: 7%;
                justify-content: center;
            }

            .header-cell.primary {
                flex: 1;
            }

            .data-cell {
                display: flex;
                align-items: center;
                padding: 0 10px;
            }

            .data-cell.text {
                text-align: left;
                width: 15%;
                overflow: hidden;
                text-overflow: ellipsis;
            }

            .data-cell.number {
                text-align: center;
                width: 7%;
                justify-content: center;
            }

            .data-cell.primary {
                flex: 1;
            }

            .action-button {
                font-size: 16px;
                border-radius: 8px;
                margin: 10px 0;
                --mdc-typography-button-text-transform: capitalize;
            }

            .aqua-blue {
                --mdc-theme-primary: var(--main-secondary-color);
            }

            .challenges-wrapper {
                width: 100%;
                overflow-y: auto;
                display: flex;
                flex-direction: column;
                align-items: center;
                justify-content: flex-start;
                box-sizing: border-box;
            }

            .challenge-row {
                width: 100%;
                display: flex;
                align-items: center;
                justify-content: space-between;
                padding: 8px 5px;
                box-sizing: border-box;
                border-bottom: 1px solid #DADADA;
                gap: 10px;
            }

            .challenge-row:hover {
                background-color: var(--theme-border-color);
                cursor: context-menu;
            }

            .user {
                color: #102C3D;
                text-align: left;
                line-height: 125%;
            }

            .stats {
                box-sizing: border-box;
                color: #102C3D;
                text-align: center;
                line-height: 125%;
            }

            .view-more {
                cursor: pointer;
                color: #102C3D;
                text-align: right;
                font-weight: 700;
                line-height: 125%;
                text-decoration-line: underline;
            }

            @media only screen and (max-width: 550px) {
                .modalContent {
                    width: 100%;
                    min-height: 100%;
                    max-height: 100%;
                }

                .terms__desc-wrapper {
                    max-height: 200px;
                }
            }
        `;
    }
}